import React, { useState, useEffect } from 'react';
import { Button, Copy } from 'components/components/atoms';
import PropTypes from 'prop-types';
import breakpoints from 'utils/breakpoints';
import cx from 'classnames';
import styled from 'styled-components';
import { useSpring, animated } from '@react-spring/web';
import { useMeasure } from 'react-use';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { isValidTitle } from 'utils/validateTitle';

const ExpansionContainer = styled.div`
  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor ? `background-color: ${newSchemaColorHexaCode};` : ''}
`;

const StyledHeading = styled.h2`
  margin-bottom: 0px;
  @media ${breakpoints.lg} {
    margin-left: 1.75rem;
  }
`;

const ContentWrapper = styled(animated.div)`
  overflow: hidden;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const ExpansionPanelCrawlerReadable = ({
  cta = {},
  features = [],
  text = '',
  title = '',
  type = 'text',
  openOnLanding = false,
  newSchemaColorHexaCode = '',
  showNewSchemaColor = false,
  compact = false,
}) => {
  const [ref, { height }] = useMeasure();
  const defaultHeight = '0px';
  const [open, toggle] = useState(openOnLanding);
  const [contentHeight, setContentHeight] = useState(
    openOnLanding ? height : defaultHeight,
  );

  const getProperHeight = () => {
    if (open) {
      if (!cta) {
        return `${contentHeight + (compact ? 20 : 40)}px`;
      }
      return `${contentHeight}px`;
    }
    return defaultHeight;
  };

  const expand = useSpring({
    height: getProperHeight(),
  });

  const fade = useSpring({
    //opacity: open ? 1 : 0,
    delay: open ? 200 : 0,
  });

  const toggleSection = () => {
    toggle(!open);
  };

  useEffect(() => {
    const updateContentHeight = () => {
      setContentHeight(height);
    };

    updateContentHeight();
    window.addEventListener('resize', updateContentHeight);

    return () => window.removeEventListener('resize', updateContentHeight);
  }, [height]);

  const renderContent = () => (
    <animated.div
      className={cx('flex flex-col', compact ? 'pl-0 md:pl-6' : '')}
      ref={ref}
      style={fade}
    >
      {type === 'grid' ? (
        <>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 pt-8'>
            {features &&
              features.map((feature) => (
                <div
                  className='p-5 flex items-center justify-center bg-white rounded max-h-10 overflow-auto'
                  key={feature.text}
                >
                  <span
                    className={
                      showNewSchemaColor ? 'text-black' : 'text-mattamy-blue'
                    }
                  >
                    <RichText
                      tag='span'
                      field={{ value: feature.text }}
                      className='richtext text-center'
                    />
                  </span>
                </div>
              ))}
          </div>
          {cta && cta.text && cta.href && (
            <a
              href={cta.href}
              target={cta.target || '_self'}
              className='mt-10'
              tabIndex={open ? '0' : '-1'}
              aria-label={cta.text}
            >
              <Button
                className={cx('w-full md:w-full ', {
                  'hover:bg-mattamy-blue-dark': !showNewSchemaColor,
                  'hover:bg-black hover:text-white': showNewSchemaColor,
                  'md:mb-5': cta && cta.text,
                })}
                variant={showNewSchemaColor ? 'outlined' : 'filled'}
                color={showNewSchemaColor ? 'black' : 'action-blue'}
                textColor={showNewSchemaColor ? 'black' : 'white'}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                showNewSchemaColor={showNewSchemaColor}
              >
                {cta.text}
              </Button>
            </a>
          )}
        </>
      ) : (
        <>
          <Copy
            className={cx(
              'w-full items-start mb-0 leading-7',
              compact ? 'pt-2 text-sm md:text-normal' : 'pt-3 md:text-lg',
              {
                'pb-4 md:mb-3': compact && cta && cta.text,
                'pb-30-mattamy md:mb-5': !compact && cta && cta.text,
              },
            )}
            color={showNewSchemaColor ? 'black' : 'blue'}
          >
            {text}
          </Copy>
          {cta && cta.text && cta.href && (
            <a
              href={cta.href}
              target={cta.target || '_self'}
              tabIndex={open ? '0' : '-1'}
              aria-label={cta.text}
            >
              <Button
                className={cx('w-full h-auto md:w-full ', {
                  'hover:bg-mattamy-blue-dark': !showNewSchemaColor,
                  'hover:bg-black hover:text-white': showNewSchemaColor,
                })}
                variant={showNewSchemaColor ? 'outlined' : 'filled'}
                color={showNewSchemaColor ? 'black' : 'action-blue'}
                textColor={showNewSchemaColor ? 'black' : 'white'}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                height={true}
              >
                {cta.text}
              </Button>
            </a>
          )}
        </>
      )}
    </animated.div>
  );

  return (
    <ExpansionContainer
      className={cx(
        'flex flex-col w-full rounded overflow-hidden',
        compact ? 'px-5 py-4 md:py-6 md:px-10' : 'p-5 xl:py-8 xl:px-20',
        {
          'lg:pb-6': compact && open,
          'xl:pb-12': !compact && open,
          'bg-light-blue ': !showNewSchemaColor,
        },
      )}
      showNewSchemaColor={showNewSchemaColor}
      newSchemaColorHexaCode={newSchemaColorHexaCode}
    >
      <div className='flex justify-between items-center'>
        {isValidTitle(title) && (
          <StyledHeading
            className={cx(
              'font-bold leading-tiny font-trade-gothic-20',
              compact
                ? 'mt-1 mb-3 md:mb-4 text-xl md:text-2xl'
                : 'mt-2 mb-6 md:mb-10-mattamy text-4xl',
              {
                'text-mattamy-blue ': !showNewSchemaColor,
                'text-black ': showNewSchemaColor,
              },
            )}
            $compact={compact}
          >
            {title}
          </StyledHeading>
        )}
        <button
          className={cx(
            'relative z-10 flex-shrink-0',
            compact ? 'w-10 h-10' : 'w-10 h-10 lg:w-20 lg:h-20',
          )}
          onClick={toggleSection}
          aria-label={
            open ? 'Collapse Information' : 'Expand for more information'
          }
          aria-expanded={open}
        >
          <div
            className={cx(
              'absolute top-0 right-0 left-0 bottom-0 m-auto w-8/12 h-1 rounded transform transition-transform duration-700',
              {
                'rotate-90': !open,
                'bg-black': showNewSchemaColor,
                'bg-action-blue': !showNewSchemaColor,
              },
            )}
          />
          <div
            className={cx(
              'absolute top-0 right-0 left-0 bottom-0 m-auto w-8/12 h-1 rounded',
              {
                'bg-black': showNewSchemaColor,
                'bg-action-blue': !showNewSchemaColor,
              },
            )}
          />
        </button>
      </div>
      <ContentWrapper style={expand} $open={open}>
        {renderContent()}
      </ContentWrapper>
    </ExpansionContainer>
  );
};

ExpansionPanelCrawlerReadable.propTypes = {
  cta: PropTypes.object,
  features: PropTypes.array,
  text: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf(['grid', 'text']),
  openOnLanding: PropTypes.bool,
  compact: PropTypes.bool,
};

export default ExpansionPanelCrawlerReadable;
